import React, { useState, useEffect } from 'react';
import { useUser } from '../../context/UserContext';
import '../admin/AdminComponents.css';

const RoleForm = ({ role, onClose }) => {
  const { permissions, addRole, updateRole } = useUser();
  const [formData, setFormData] = useState({
    id: '',
    name: '',
    description: '',
    permissions: []
  });
  const [errors, setErrors] = useState({});

  useEffect(() => {
    if (role) {
      setFormData({
        id: role.id || '',
        name: role.name || '',
        description: role.description || '',
        permissions: role.id === 'viewer' ? [] : (role.permissions || [])
      });
    } else {
      setFormData({
        id: '',
        name: '',
        description: '',
        permissions: []
      });
    }
  }, [role]);

  const isViewerRole = (role?.id || formData.id) === 'viewer';

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));

    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  const handlePermissionToggle = (permissionKey) => {
    if (isViewerRole) return;
    setFormData(prev => ({
      ...prev,
      permissions: prev.permissions.includes(permissionKey)
        ? prev.permissions.filter(p => p !== permissionKey)
        : [...prev.permissions, permissionKey]
    }));
  };

  const handleSelectAllInCategory = (category) => {
    if (isViewerRole) return;
    const categoryPermissions = Object.entries(permissions)
      .filter(([_, perm]) => perm.category === category)
      .map(([key]) => key);

    const allSelected = categoryPermissions.every(p => formData.permissions.includes(p));

    if (allSelected) {
      setFormData(prev => ({
        ...prev,
        permissions: prev.permissions.filter(p => !categoryPermissions.includes(p))
      }));
    } else {
      setFormData(prev => ({
        ...prev,
        permissions: [...new Set([...prev.permissions, ...categoryPermissions])]
      }));
    }
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData.name.trim()) {
      newErrors.name = 'Role name is required';
    }

    if (!role) {
      const roleId = formData.id.trim();
      if (!roleId) {
        newErrors.id = 'Role ID is required';
      } else if (roleId.length > 50) {
        newErrors.id = 'Role ID must be 50 characters or fewer';
      } else if (!/^[a-z0-9_-]+$/.test(roleId)) {
        newErrors.id = 'Use lowercase letters, numbers, hyphens, or underscores only';
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    const roleData = {
      ...formData,
      id: formData.id.trim(),
      permissions: isViewerRole ? [] : formData.permissions
    };

    try {
      if (role) {
        await updateRole(role.id, roleData);
      } else {
        await addRole(roleData);
      }
      onClose();
    } catch (error) {
      alert(error.message || 'Failed to save role');
    }
  };

  const permissionsByCategory = {};
  Object.entries(permissions).forEach(([key, perm]) => {
    const category = perm.category || 'Other';
    if (!permissionsByCategory[category]) {
      permissionsByCategory[category] = [];
    }
    permissionsByCategory[category].push({ key, ...perm });
  });

  return (
    <div className="admin-modal-overlay" onClick={onClose}>
      <div className="admin-modal-content" onClick={(e) => e.stopPropagation()} style={{ maxWidth: '900px' }}>
        <div className="admin-modal-header">
          <h3 className="admin-modal-title">
            {role ? 'Edit Role' : 'Add Role'}
          </h3>
          <button className="admin-modal-close" onClick={onClose}>×</button>
        </div>
        <div className="admin-modal-body">
          <form className="admin-form" onSubmit={handleSubmit}>
            <div className="admin-form-group">
              <label className="admin-form-label">ID *</label>
              <input
                type="text"
                name="id"
                className={`admin-form-input ${errors.id ? 'error' : ''}`}
                value={formData.id}
                onChange={handleChange}
                disabled={!!role}
                required
                maxLength="50"
                placeholder="e.g., content-creator"
              />
              {role && (
                <small className="admin-form-hint">Role ID cannot be changed</small>
              )}
              {!role && !errors.id && (
                <small className="admin-form-hint">
                  Lowercase letters, numbers, hyphens, and underscores only
                </small>
              )}
              {errors.id && <span className="admin-error-message">{errors.id}</span>}
            </div>
            <div className="admin-form-group">
              <label className="admin-form-label">Name *</label>
              <input
                type="text"
                name="name"
                className={`admin-form-input ${errors.name ? 'error' : ''}`}
                value={formData.name}
                onChange={handleChange}
                required
                placeholder="e.g., Editor, Viewer"
              />
              {errors.name && <span className="admin-error-message">{errors.name}</span>}
            </div>
            <div className="admin-form-group">
              <label className="admin-form-label">Description</label>
              <textarea
                name="description"
                className="admin-form-textarea"
                value={formData.description}
                onChange={handleChange}
                rows="3"
                placeholder="Role description..."
              />
            </div>
            <div className="admin-form-group">
              <label className="admin-form-label">Permissions</label>
              {isViewerRole && (
                <small className="admin-form-hint">
                  Viewer is the default public role and cannot be granted administrative permissions.
                </small>
              )}
              <div className="admin-permissions-list">
                {Object.entries(permissionsByCategory).map(([category, perms]) => (
                  <div key={category} className="admin-permission-category">
                    <div className="admin-permission-category-header">
                      <h4 className="admin-permission-category-title">{category}</h4>
                      <button
                        type="button"
                        className="admin-button-link"
                        onClick={() => handleSelectAllInCategory(category)}
                        disabled={isViewerRole}
                      >
                        Select All / None
                      </button>
                    </div>
                    <div className="admin-permission-options">
                      {perms.map(({ key, name }) => (
                        <label key={key} className="admin-permission-option">
                          <input
                            type="checkbox"
                            checked={formData.permissions.includes(key)}
                            onChange={() => handlePermissionToggle(key)}
                            disabled={isViewerRole}
                          />
                          <span>{name}</span>
                        </label>
                      ))}
                    </div>
                  </div>
                ))}
              </div>

            </div>


            <div className="admin-form-actions">
              <button type="button" className="admin-button" onClick={onClose}>
                Cancel
              </button>

              <button type="submit" className="admin-button primary">
                {role ? 'Update' : 'Create'}
              </button>

            </div>

          </form>

        </div>

      </div>

    </div>

  );
};

export default RoleForm;
